import { useQuery } from '@tanstack/react-query';
import {
  fetchRepositories,
  fetchAllWorkflowRuns,
  fetchAllPullRequests,
  fetchGitHubStats,
  fetchRepoWorkflowRuns,
  fetchBuildsStats,
  fetchGitHubCollectorStatus,
  fetchAnalyticsSummary,
  fetchPRActivity,
  fetchHotfixes,
  fetchZeroReviews,
  fetchBuildFailures,
  fetchForceMerges,
} from '@/api/github';
import type { RepoListParams, WorkflowRunParams, PullRequestParams, BuildsStatsParams, AnalyticsParams } from '@/api/github';

export function useRepositories(params?: RepoListParams) {
  return useQuery({
    queryKey: ['repositories', params],
    queryFn: () => fetchRepositories(params),
    staleTime: 5 * 60 * 1000,
  });
}

export function useWorkflowRuns(params?: WorkflowRunParams) {
  return useQuery({
    queryKey: ['workflow-runs', params],
    queryFn: () => fetchAllWorkflowRuns(params),
    staleTime: 30 * 1000,
  });
}

export function useRepoWorkflowRuns(repoId: string, params?: WorkflowRunParams) {
  return useQuery({
    queryKey: ['repo-workflow-runs', repoId, params],
    queryFn: () => fetchRepoWorkflowRuns(repoId, params),
    staleTime: 30 * 1000,
    enabled: !!repoId,
  });
}

export function usePullRequests(params?: PullRequestParams) {
  return useQuery({
    queryKey: ['pull-requests', params],
    queryFn: () => fetchAllPullRequests(params),
    staleTime: 5 * 60 * 1000,
  });
}

export function useGitHubStats() {
  return useQuery({
    queryKey: ['github-stats'],
    queryFn: fetchGitHubStats,
    staleTime: 2 * 60 * 1000,
  });
}

export function useBuildsStats(params?: BuildsStatsParams) {
  return useQuery({
    queryKey: ['builds-stats', params],
    queryFn: () => fetchBuildsStats(params),
    staleTime: 60 * 1000,
    // Refetch periodically while syncing so counts climb automatically
    refetchInterval: 20 * 1000,
  });
}

export function useGitHubCollectorStatus() {
  return useQuery({
    queryKey: ['collector-status', 'github'],
    queryFn: fetchGitHubCollectorStatus,
    staleTime: 15 * 1000,
    refetchInterval: 15 * 1000,
  });
}

export function useAnalyticsSummary(params?: AnalyticsParams) {
  return useQuery({
    queryKey: ['analytics-summary', params],
    queryFn: () => fetchAnalyticsSummary(params),
    staleTime: 5 * 60 * 1000,
  });
}

export function usePRActivity(params?: AnalyticsParams) {
  return useQuery({
    queryKey: ['analytics-pr-activity', params],
    queryFn: () => fetchPRActivity(params),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAnalyticsHotfixes(params?: AnalyticsParams) {
  return useQuery({
    queryKey: ['analytics-hotfixes', params],
    queryFn: () => fetchHotfixes(params),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAnalyticsZeroReviews(params?: AnalyticsParams) {
  return useQuery({
    queryKey: ['analytics-zero-reviews', params],
    queryFn: () => fetchZeroReviews(params),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAnalyticsBuildFailures(params?: AnalyticsParams) {
  return useQuery({
    queryKey: ['analytics-build-failures', params],
    queryFn: () => fetchBuildFailures(params),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAnalyticsForceMerges(params?: AnalyticsParams) {
  return useQuery({
    queryKey: ['analytics-force-merges', params],
    queryFn: () => fetchForceMerges(params),
    staleTime: 5 * 60 * 1000,
  });
}
